import { IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { ViewPage } from "../../../../../../assets/icons";
import { getNodeTypeIcon } from "../../../../../../constants/nodeTypeIcons";
import { NodesDetailsPageItemDTO, NodesPageDTO } from "../../../../../../datasources/api-client/data-contracts";

export type MasterDataOperationalUnitsRows = {
	id: number;
	description?: string;
	type?: NodeType;
	address?: string;
	code?: string;
	city?: string;
	country?: string;
};

type NodeType = {
	label?: string;
	typeId?: number;
};

export const getRows = (data: NodesPageDTO): MasterDataOperationalUnitsRows[] => {
	if (!data) return [];

	return data.items.map((item: NodesDetailsPageItemDTO) => {
		return {
			id: item.id,
			code: item.code,
			description: item.description,
			type: {
				label: item.typeDescription,
				typeId: item.typeId,
			},
			address: item.address,
			zipCode: item.zipCode,
			city: item.city,
			country: item.country,
		};
	});
};

export const colsData = (): GridColDef[] => [
	{
		field: "code",
		headerName: "operational unit",
		hideable: true,
		flex: 0.7,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "description",
		headerName: "description",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "type",
		headerName: "nodeType",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			const Icon = getNodeTypeIcon(params.value.typeId);
			return (
				<Stack height="100%" alignItems="center" justifyContent="start" direction={"row"} spacing={1}>
					{Icon && <Icon />}
					<Typography variant="body2">{params.value?.label || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "address",
		headerName: "address",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "zipCode",
		headerName: "zip code",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "city",
		headerName: "city",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "country",
		headerName: "country",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value || "-"}</Typography>
				</Stack>
			);
		},
	},
];

export const getColumns = (
	dataCol: GridColDef[],
	headerTextColor: string,
	t: TFunction,
	onNavigate: (id: string, code?: string) => void
): GridColDef[] => {
	const baseCols = dataCol.map((col) => {
		const label = t(`tables header/${col.headerName}`);
		return {
			...col,
			headerName: label,
			renderHeader: () => (
				<Stack direction="column" width="100%">
					<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
						{label}
					</Typography>
				</Stack>
			),
		};
	});

	const actionCol: GridColDef = {
		field: "actions",
		headerName: t("common:actions"),
		hideable: true,
		sortable: false,
		width: 100,
		align: "center",
		renderHeader: () => (
			<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
				{t("common:actions")}
			</Typography>
		),
		renderCell: (params) => {
			const id = params.row.id;
			const code = params.row.code;

			return (
				<IconButton
					onClick={(e) => {
						e.stopPropagation();
						onNavigate(id, code);
					}}
				>
					<ViewPage fontSize="small" />
				</IconButton>
			);
		},
	};

	return [...baseCols, actionCol];
};
